import React, { useState } from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Tag,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { DELIVERY_LABELS } from "../../../../constants/OrderConstant";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

const { Option } = Select;

const DAY_NAMES = {
  0: "week_days.sunday",
  1: "week_days.monday",
  2: "week_days.tuesday",
  3: "week_days.wednesday",
  4: "week_days.thursday",
  5: "week_days.friday",
  6: "week_days.saturday",
};

const DeliveryField = ({ mode }) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const deliveryLabels = Form.useWatch(["delivery", "deliveryLabels"], form) || [];
  const isViewMode = mode === "VIEW";
  const [activeKeys, setActiveKeys] = useState([]);

  if (isViewMode) {
    const delivery = form.getFieldValue(["delivery"]) || {};
    const deliveryLabelsData = delivery.deliveryLabels || [];

    return (
      <Row gutter={16}>
        <Col span={24}>
          <Card title={t("countries.delivery_tab")} style={{ maxWidth: 600 }}>
            <Descriptions column={{ xs: 1, sm: 2, md: 2 }} bordered size="small">
              <Descriptions.Item label={t("countries.delivery_default_cost")}>
                {delivery.defaultDeliveryCost !== undefined ? `€${delivery.defaultDeliveryCost}` : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("countries.delivery_free_above")}>
                {delivery.deliveryCostThreshold !== undefined ? `€${delivery.deliveryCostThreshold}` : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("countries.default_delivery_label")}>
                {delivery.defaultLabel
                  ? DELIVERY_LABELS.find((l) => l.code === delivery.defaultLabel)?.label || delivery.defaultLabel
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("countries.cmr_document_required")}>
                {delivery.cmrRequired ? (
                  <Tag color="green" icon={<CheckOutlined />}>
                    {t("yes")}
                  </Tag>
                ) : (
                  <Tag icon={<CloseOutlined />}>{t("no")}</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={24} style={{ marginTop: 16 }}>
          <Card title={t("countries.delivery_labels_configuration")}>
            {deliveryLabelsData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                {t("countries.no_delivery_labels_configured")}
              </div>
            ) : (
              deliveryLabelsData.map((label, index) => {
                const labelInfo = DELIVERY_LABELS.find((l) => l.code === label.deliveryLabel);
                return (
                  <Card
                    key={index}
                    type="inner"
                    title={
                      <Space>
                        <span>{t("countries.delivery_label")}</span>
                        {labelInfo && <span style={{ fontWeight: "normal" }}>({labelInfo.label})</span>}
                      </Space>
                    }
                    style={{ marginBottom: 16 }}
                  >
                    <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
                      <Descriptions.Item label={t("countries.delivery_label_code")}>
                        {labelInfo?.label || label.deliveryLabel || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label={t("countries.delivery_days")}>
                        {label.deliveryDays && label.deliveryDays.length > 0
                          ? label.deliveryDays.map((day) => t(DAY_NAMES[day])).join(", ")
                          : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label={t("active")}>
                        {label.isActive ? (
                          <Tag color="green" icon={<CheckOutlined />}>
                            {t("yes")}
                          </Tag>
                        ) : (
                          <Tag icon={<CloseOutlined />}>{t("no")}</Tag>
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label={t("countries.delivery_lead_days")}>
                        {label.leadDays !== undefined ? `${label.leadDays} ${t("days")}` : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label={t("countries.delivery_cutoff_time")}>
                        {label.cutoffTime || "-"}
                      </Descriptions.Item>
                    </Descriptions>

                    {label.deliveryTimeSlots && label.deliveryTimeSlots.length > 0 && (
                      <>
                        <Divider orientation="left">{t("countries.time_slots")}</Divider>
                        {label.deliveryTimeSlots.map((slot, slotIndex) => (
                          <Card key={slotIndex} type="inner" size="small" style={{ marginBottom: 8 }}>
                            <Descriptions column={{ xs: 1, sm: 3 }} size="small">
                              <Descriptions.Item label={t("countries.slot_start_time")}>
                                {slot.start || "-"}
                              </Descriptions.Item>
                              <Descriptions.Item label={t("countries.slot_end_time")}>
                                {slot.end || "-"}
                              </Descriptions.Item>
                              <Descriptions.Item label={t("countries.slot_surcharge")}>
                                {slot.deliverySurcharge !== undefined ? `€${slot.deliverySurcharge}` : "-"}
                              </Descriptions.Item>
                            </Descriptions>
                          </Card>
                        ))}
                      </>
                    )}
                  </Card>
                );
              })
            )}
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row gutter={16}>
      <Col span={24}>
        <Card title={t("countries.delivery_tab")}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name={["delivery", "defaultDeliveryCost"]} label={t("countries.delivery_default_cost")}>
                <InputNumber style={{ width: "100%" }} min={0} step={0.5} precision={2} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name={["delivery", "deliveryCostThreshold"]} label={t("countries.delivery_free_above")}>
                <InputNumber style={{ width: "100%" }} min={0} step={1} precision={2} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name={["delivery", "defaultLabel"]} label={t("countries.default_delivery_label")}>
                <Select
                  placeholder={t("countries.select_default_delivery_label")}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {deliveryLabels.map((dl, index) => (
                    <Option key={index} value={dl.deliveryLabel}>
                      {DELIVERY_LABELS.find((l) => l.code === dl.deliveryLabel)?.label || dl.deliveryLabel}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name={["delivery", "cmrRequired"]}
                label={t("countries.cmr_document_required")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Col>
      <Col span={24} style={{ marginTop: 16 }}>
        <Card title={t("countries.delivery_labels_configuration")}>
          <Form.List name={["delivery", "deliveryLabels"]}>
            {(fields, { add, remove }) => {
              const handleAdd = () => {
                add({ deliveryLabel: "", deliveryDays: [], deliveryTimeSlots: [], cutoffTime: "", isActive: true });
                setActiveKeys((prev) => [...prev, String(fields.length)]);
              };

              const handleRemove = (fieldName, index) => {
                remove(fieldName);
                setActiveKeys((prev) => {
                  const filtered = prev.filter((k) => k !== String(index));
                  return filtered.map((k) => (parseInt(k) > index ? String(parseInt(k) - 1) : k));
                });
              };

              return (
                <>
                  {fields.length === 0 && (
                    <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                      {t("countries.no_delivery_labels_configured")}
                    </div>
                  )}
                  {fields.length > 0 && (
                    <Collapse
                      activeKey={activeKeys}
                      onChange={setActiveKeys}
                      style={{ marginBottom: 16 }}
                      items={fields.map((field, index) => {
                        const labelValue = form.getFieldValue([
                          "delivery",
                          "deliveryLabels",
                          field.name,
                          "deliveryLabel",
                        ]);
                        const labelInfo = DELIVERY_LABELS.find((l) => l.code === labelValue);

                        return {
                          key: String(index),
                          label: (
                            <Space>
                              <span>{t("countries.delivery_label")}</span>
                              {labelInfo ? (
                                <span style={{ fontWeight: "normal" }}>({labelInfo.label})</span>
                              ) : (
                                <span style={{ fontWeight: "normal", color: "#999" }}>
                                  ({t("countries.new_delivery_label")})
                                </span>
                              )}
                            </Space>
                          ),
                          extra: (
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(field.name, index);
                              }}
                            >
                              {t("delete")}
                            </Button>
                          ),
                          children: (
                            <>
                              <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                  <Form.Item
                                    name={[field.name, "deliveryLabel"]}
                                    label={t("countries.delivery_label_code")}
                                    rules={[{ required: true, message: t("required") }]}
                                  >
                                    <Select
                                      placeholder={t("countries.select_delivery_label")}
                                      showSearch
                                      optionFilterProp="children"
                                    >
                                      {DELIVERY_LABELS.map((dl) => (
                                        <Option key={dl.code} value={dl.code}>
                                          {dl.label}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                  <Form.Item name={[field.name, "deliveryDays"]} label={t("countries.delivery_days")}>
                                    <Select
                                      mode="multiple"
                                      placeholder={t("countries.select_delivery_days")}
                                      allowClear
                                    >
                                      <Option value={1}>{t("week_days.monday")}</Option>
                                      <Option value={2}>{t("week_days.tuesday")}</Option>
                                      <Option value={3}>{t("week_days.wednesday")}</Option>
                                      <Option value={4}>{t("week_days.thursday")}</Option>
                                      <Option value={5}>{t("week_days.friday")}</Option>
                                      <Option value={6}>{t("week_days.saturday")}</Option>
                                      <Option value={0}>{t("week_days.sunday")}</Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                  <Form.Item
                                    name={[field.name, "isActive"]}
                                    label={t("active")}
                                    valuePropName="checked"
                                  >
                                    <Switch />
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Divider orientation="left">{t("countries.cutoff_and_lead_time")}</Divider>
                              <Row gutter={16}>
                                <Col xs={24} sm={8}>
                                  <Form.Item name={[field.name, "leadDays"]} label={t("countries.delivery_lead_days")}>
                                    <InputNumber style={{ width: "100%" }} min={0} max={6} precision={0} />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} sm={8}>
                                  <Form.Item
                                    name={[field.name, "cutoffTime"]}
                                    label={t("countries.delivery_cutoff_time")}
                                  >
                                    <Input style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Divider orientation="left">{t("countries.time_slots")}</Divider>
                              <div style={{ marginBottom: 12, color: "rgba(0,0,0,.45)", fontSize: "13px" }}>
                                <InfoCircleOutlined style={{ marginRight: 6 }} />
                                {t("countries.time_slot_default_info")}
                              </div>
                              <Form.List name={[field.name, "deliveryTimeSlots"]}>
                                {(slotFields, { add: addSlot, remove: removeSlot, move: moveSlot }) => {
                                  const handleDragEnd = (result) => {
                                    if (!result.destination) return;
                                    const sourceIndex = result.source.index;
                                    const destIndex = result.destination.index;
                                    if (sourceIndex !== destIndex) {
                                      moveSlot(sourceIndex, destIndex);
                                    }
                                  };

                                  return (
                                    <>
                                      <DragDropContext onDragEnd={handleDragEnd}>
                                        <Droppable droppableId={`timeslots-${field.name}`}>
                                          {(provided) => (
                                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                              {slotFields.map((slotField, index) => (
                                                <Draggable
                                                  key={slotField.key}
                                                  draggableId={`slot-${slotField.key}`}
                                                  index={index}
                                                >
                                                  {(provided, snapshot) => (
                                                    <div
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      style={{
                                                        ...provided.draggableProps.style,
                                                        marginBottom: 8,
                                                      }}
                                                    >
                                                      <Row
                                                        gutter={16}
                                                        style={{
                                                          padding: "8px",
                                                          backgroundColor: snapshot.isDragging
                                                            ? "#f0f0f0"
                                                            : "transparent",
                                                          border: snapshot.isDragging ? "1px dashed #1890ff" : "none",
                                                          borderRadius: "4px",
                                                        }}
                                                      >
                                                        <Col
                                                          xs={24}
                                                          sm={1}
                                                          style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            cursor: "move",
                                                          }}
                                                          {...provided.dragHandleProps}
                                                        >
                                                          <HolderOutlined style={{ fontSize: "16px", color: "#999" }} />
                                                        </Col>
                                                        <Col xs={24} sm={7}>
                                                          <Form.Item
                                                            name={[slotField.name, "start"]}
                                                            label={t("countries.slot_start_time")}
                                                          >
                                                            <Input placeholder="08:00" />
                                                          </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={7}>
                                                          <Form.Item
                                                            name={[slotField.name, "end"]}
                                                            label={t("countries.slot_end_time")}
                                                          >
                                                            <Input placeholder="12:00" />
                                                          </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={6}>
                                                          <Form.Item
                                                            name={[slotField.name, "deliverySurcharge"]}
                                                            label={t("countries.slot_surcharge")}
                                                          >
                                                            <InputNumber
                                                              style={{ width: "100%" }}
                                                              min={0}
                                                              step={0.5}
                                                              precision={2}
                                                              prefix="€"
                                                            />
                                                          </Form.Item>
                                                        </Col>
                                                        <Col
                                                          xs={24}
                                                          sm={3}
                                                          style={{ display: "flex", alignItems: "flex-end" }}
                                                        >
                                                          <Form.Item>
                                                            <Button
                                                              type="text"
                                                              danger
                                                              icon={<DeleteOutlined />}
                                                              onClick={() => removeSlot(slotField.name)}
                                                            />
                                                          </Form.Item>
                                                        </Col>
                                                      </Row>
                                                    </div>
                                                  )}
                                                </Draggable>
                                              ))}
                                              {provided.placeholder}
                                            </div>
                                          )}
                                        </Droppable>
                                      </DragDropContext>
                                      <Button
                                        type="dashed"
                                        onClick={() => addSlot({ start: "", end: "", deliverySurcharge: 0 })}
                                        icon={<PlusOutlined />}
                                        style={{ width: "100%", marginTop: 8 }}
                                      >
                                        {t("countries.add_time_slot")}
                                      </Button>
                                    </>
                                  );
                                }}
                              </Form.List>
                            </>
                          ),
                        };
                      })}
                    />
                  )}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={handleAdd} style={{ width: "100%" }}>
                    {t("countries.add_delivery_label")}
                  </Button>
                </>
              );
            }}
          </Form.List>
        </Card>
      </Col>
    </Row>
  );
};

export default DeliveryField;
